import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../../services/apiClient";
import { fleetService } from "../../services/fleetService";
import { useI18n } from "../../providers/I18nProvider";

const pad = (n) => String(n).padStart(2, "0");

const toLocalInputValue = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const ReservationFormModal = ({
  vehicle,
  initialStart,
  currentUserId,
  existingReservation, // düzenleme modu için
  calendarReservations = [], // araç takviminde görünen rezervasyonlar (çakışma ön uyarısı için)
  onClose,
  onCreated,
  onUpdated,
}) => {
  const { t } = useI18n();
  const isEdit = Boolean(existingReservation);

  const defaultStart = useMemo(() => {
    if (isEdit) return new Date(existingReservation.starts_at);
    if (initialStart) return initialStart;
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    return d;
  }, [initialStart, isEdit, existingReservation]);

  const defaultEnd = useMemo(() => {
    if (isEdit) return new Date(existingReservation.ends_at);
    const d = new Date(defaultStart);
    d.setHours(d.getHours() + 1);
    return d;
  }, [defaultStart, isEdit, existingReservation]);

  const [startsAt, setStartsAt] = useState(toLocalInputValue(defaultStart));
  const [endsAt, setEndsAt] = useState(toLocalInputValue(defaultEnd));
  const [reason, setReason] = useState(isEdit ? existingReservation.reason || "" : "");
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(
    isEdit && Array.isArray(existingReservation.participants)
      ? existingReservation.participants.map((p) => p.id)
      : []
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [myTeamId, setMyTeamId] = useState(null);

  useEffect(() => {
    // Cross-team davete izin veren dedicated endpoint
    fleetService
      .participantCandidates()
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setUsers(arr.filter((u) => u.id !== currentUserId));
        // Sahibin team_id'sini çıkar (adaylar arasında yok — /users/me'den almak da olur,
        // ama çoğu durumda seçimde görsel ipucu için bir şey şart değil)
      })
      .catch(() => setUsers([]));
  }, [currentUserId]);

  // Kullanıcının kendi team_id'sini almak için ayrı küçük çağrı (cross-team rozetini göstermek için)
  useEffect(() => {
    apiClient
      .get("/users/me")
      .then((r) => setMyTeamId(r.data?.team_id ?? null))
      .catch(() => {});
  }, []);

  // Esc ile kapat
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, submitting]);

  const startMs = startsAt ? new Date(startsAt).getTime() : NaN;
  const endMs = endsAt ? new Date(endsAt).getTime() : NaN;
  const rangeValid = Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs;
  const startInPast = !isEdit && Number.isFinite(startMs) && startMs < Date.now();

  // İstemci tarafı çakışma ön uyarısı (kesin kontrol sunucuda; 409 yine işlenir)
  const hasClientConflict = useMemo(() => {
    if (!rangeValid) return false;
    return calendarReservations.some((r) => {
      if (isEdit && r.id === existingReservation.id) return false;
      if (r.status === "cancelled" || r.status === "rejected") return false;
      return new Date(r.starts_at).getTime() < endMs && new Date(r.ends_at).getTime() > startMs;
    });
  }, [calendarReservations, rangeValid, startMs, endMs, isEdit, existingReservation]);

  const durationLabel = useMemo(() => {
    if (!rangeValid) return "";
    const mins = Math.round((endMs - startMs) / 60000);
    const d = Math.floor(mins / 1440);
    const h = Math.floor((mins % 1440) / 60);
    const m = mins % 60;
    return [d && `${d} g`, h && `${h} sa`, m && `${m} dk`].filter(Boolean).join(" ");
  }, [rangeValid, startMs, endMs]);

  const setDuration = (hours) => {
    if (!Number.isFinite(startMs)) return;
    setEndsAt(toLocalInputValue(new Date(startMs + hours * 3600000)));
  };
  const setEndOfDay = () => {
    if (!Number.isFinite(startMs)) return;
    const d = new Date(startMs);
    d.setHours(18, 0, 0, 0);
    if (d.getTime() <= startMs) d.setHours(23, 59, 0, 0);
    setEndsAt(toLocalInputValue(d));
  };

  // Başlangıç değişince süreyi koru (bitiş başlangıcın gerisinde kalmasın)
  const onStartChange = (value) => {
    const newStart = new Date(value).getTime();
    if (Number.isFinite(newStart) && Number.isFinite(startMs) && Number.isFinite(endMs) && endMs > startMs) {
      setEndsAt(toLocalInputValue(new Date(newStart + (endMs - startMs))));
    }
    setStartsAt(value);
  };

  const toggleUser = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      `${u.name || ""} ${u.surname || ""} ${u.email || ""} ${u.team_name || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [users, userSearch]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError("");
    if (!rangeValid) {
      setError(t("fleetUi.form.endBeforeStart"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        reason: reason.trim() || null,
        participant_user_ids: selectedIds,
      };
      if (isEdit) {
        const updated = await fleetService.updateReservation(
          existingReservation.id,
          payload
        );
        if (onUpdated) onUpdated(updated);
      } else {
        const created = await fleetService.createReservation({
          vehicle_id: vehicle.id,
          ...payload,
        });
        onCreated(created);
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        setError(t("fleet.errors.conflict"));
      } else {
        setError(err.response?.data?.msg || t("fleet.errors.generic"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fleet-modal-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div className="fleet-modal" role="dialog" aria-modal="true">
        <h3>
          {isEdit ? t("fleet.editReservation") : t("fleet.newReservation")} — {vehicle.name}
        </h3>
        <form onSubmit={submit}>
          <div className="fleet-field">
            <label>{t("fleet.startsAt")}</label>
            <input
              type="datetime-local"
              value={startsAt}
              onChange={(e) => onStartChange(e.target.value)}
              required
            />
            {startInPast && <small className="fleet-warn">{t("fleetUi.form.startInPast")}</small>}
          </div>
          <div className="fleet-field">
            <label>{t("fleet.endsAt")}</label>
            <input
              type="datetime-local"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              min={startsAt}
              required
              aria-invalid={!rangeValid}
            />
            <div className="fleet-quick-durations" role="group" aria-label={t("fleetUi.form.quickDuration")}>
              <button type="button" className="fleet-chip" onClick={() => setDuration(1)}>
                {t("fleetUi.form.hours1")}
              </button>
              <button type="button" className="fleet-chip" onClick={() => setDuration(2)}>
                {t("fleetUi.form.hours2")}
              </button>
              <button type="button" className="fleet-chip" onClick={() => setDuration(4)}>
                {t("fleetUi.form.hours4")}
              </button>
              <button type="button" className="fleet-chip" onClick={setEndOfDay}>
                {t("fleetUi.form.endOfDay")}
              </button>
              {durationLabel && (
                <span className="fleet-duration">{t("fleetUi.form.duration", { value: durationLabel })}</span>
              )}
            </div>
            {!rangeValid && Number.isFinite(startMs) && Number.isFinite(endMs) && (
              <small className="fleet-warn">{t("fleetUi.form.endBeforeStart")}</small>
            )}
            {hasClientConflict && <small className="fleet-warn">{t("fleetUi.form.clientConflict")}</small>}
          </div>
          <div className="fleet-field">
            <label>{t("fleet.reason")}</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("fleet.reasonPlaceholder")}
            />
          </div>
          <div className="fleet-field">
            <label>
              {t("fleet.participants")}{" "}
              <small style={{ opacity: 0.7 }}>({selectedIds.length})</small>
            </label>
            <input
              type="text"
              placeholder={t("fleet.searchUser")}
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              style={{ marginBottom: 6 }}
            />
            <div className="participants-chooser">
              {filteredUsers.length === 0 && (
                <small style={{ opacity: 0.7 }}>
                  {t("fleet.noUsersFound")}
                </small>
              )}
              {filteredUsers.map((u) => {
                const isCrossTeam =
                  myTeamId != null && u.team_id != null && u.team_id !== myTeamId;
                return (
                  <label key={u.id} className="participant-row">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                    />
                    <span>
                      {u.name} {u.surname}
                      {u.email ? ` — ${u.email}` : ""}
                      {u.team_name ? (
                        <small
                          style={{
                            marginLeft: 6,
                            opacity: 0.7,
                            fontStyle: isCrossTeam ? "italic" : "normal",
                          }}
                        >
                          [{u.team_name}
                          {isCrossTeam ? ` • ${t("fleet.crossTeam")}` : ""}]
                        </small>
                      ) : null}
                    </span>
                  </label>
                );
              })}
            </div>
            {myTeamId != null &&
              selectedIds.some((id) => {
                const u = users.find((x) => x.id === id);
                return u && u.team_id != null && u.team_id !== myTeamId;
              }) && (
                <small style={{ opacity: 0.8, marginTop: 4, display: "block" }}>
                  {t("fleet.crossTeamNotice")}
                </small>
              )}
          </div>
          {error && (
            <div className="fleet-error" role="alert">
              {error}
            </div>
          )}
          <div className="fleet-modal-actions">
            <button
              type="button"
              className="fleet-btn secondary"
              onClick={onClose}
            >
              {t("common.cancel")}
            </button>
            <button type="submit" className="fleet-btn" disabled={submitting || !rangeValid}>
              {submitting
                ? t("common.loading")
                : isEdit
                  ? t("common.save")
                  : t("fleet.createReservation")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReservationFormModal;
